import React, { useEffect, useState } from "react";
import { Monitor, Download } from "lucide-react";

interface DownloadAsset {
	platform: string;
	filename: string;
	size: number;
}

interface DownloadInfo {
	version: string;
	app: string;
	assets: DownloadAsset[];
}

function detectPlatform(): "win32" | "darwin" | "linux" {
	const ua = navigator.userAgent.toLowerCase();
	if (ua.includes("win")) return "win32";
	if (ua.includes("mac")) return "darwin";
	return "linux";
}

function formatSize(bytes: number): string {
	if (bytes === 0) return "";
	const mb = bytes / (1024 * 1024);
	return `${Math.round(mb)} MB`;
}

const PLATFORM_LABELS: Record<string, string> = {
	win32: "Windows",
	darwin: "macOS",
	linux: "Linux",
};

export const DesktopDownloadHero: React.FC = () => {
	const [downloadInfo, setDownloadInfo] = useState<DownloadInfo | null>(null);
	const detectedPlatform = detectPlatform();
	const backendUrl =
		import.meta.env.VITE_PDF_MICROSERVICE_URL || "http://localhost:8400";

	useEffect(() => {
		fetch(`${backendUrl}/desktop-downloads/dentlab-desktop`)
			.then((res) => res.json())
			.then((data) => {
				if (data?.assets) setDownloadInfo(data);
			})
			.catch(() => {});
	}, []);

	function handleDownload(platform: string) {
		window.open(
			`${backendUrl}/desktop-downloads/dentlab-desktop/${platform}`,
			"_blank"
		);
	}

	return (
		<div
			className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center bg-gray-50"
			data-testid="desktop-download-hero"
		>
			<Monitor className="h-12 w-12 text-gray-400 mx-auto mb-4" />
			<h2 className="text-xl font-semibold text-gray-900 mb-2">
				dentLAB Druckservice einrichten
			</h2>
			<p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
				Installieren Sie den dentLAB Druckservice, um Druckaufträge
				direkt aus dentLAB an Ihre lokalen Drucker zu senden.
			</p>

			<ol className="text-sm text-gray-600 text-left max-w-sm mx-auto mb-6 space-y-1">
				<li>1. Laden Sie die App für Ihr Betriebssystem herunter</li>
				<li>2. Installieren und öffnen Sie die App</li>
				<li>3. Melden Sie sich mit Ihrem dentLAB-Konto an</li>
				<li>4. Die App verbindet sich automatisch mit Ihrem Labor</li>
			</ol>

			<div className="flex justify-center gap-3 mb-4">
				{(["win32", "darwin", "linux"] as const).map((platform) => {
					const asset = downloadInfo?.assets.find(
						(a) => a.platform === platform
					);
					const isPrimary = platform === detectedPlatform;
					const sizeText = asset
						? ` (${formatSize(asset.size)})`
						: "";

					return (
						<button
							key={platform}
							onClick={() => handleDownload(platform)}
							className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
								isPrimary
									? "bg-primary text-white hover:bg-primary/90"
									: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
							}`}
							data-testid={`download-button-${platform}`}
							aria-label={`Download für ${PLATFORM_LABELS[platform]}`}
						>
							<Download className="h-4 w-4" />
							{PLATFORM_LABELS[platform]}
							{sizeText}
						</button>
					);
				})}
			</div>

			{downloadInfo?.version && (
				<div className="text-xs text-gray-400">
					Version {downloadInfo.version}
				</div>
			)}
		</div>
	);
};
